import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { format, formatISO } from "date-fns";
import React from "react";
import { useAppSelector } from "store/hooks";
import {
  useArchiveCallBackEventMutation,
  useArchiveOnCallEventMutation,
  useCreateCallBackEventMutation,
  useCreateOnCallEventMutation,
} from "store/reducers";
import { selectIsManager } from "store/reducers/config/configSlice";

import { Button } from "../components/button";
import { IconButton } from "../components/iconButton";
import { TableCell } from "./table_components/table_cell";
import { TimeInput } from "./table_components/time_input";

type RecordedEvent = OnCallEvent | CallBackEvent;
type EventType = "on-call" | "call-back";

const eventDetails = {
  "on-call": { icon: "user-clock" as const, label: "On-Call" },
  "call-back": { icon: "phone" as const, label: "Call-Back" },
};

export function getRecordedEventSortTime(event: RecordedEvent) {
  return new Date(event.recorded_at).getTime();
}

type RecordedEventRowProps = {
  event: RecordedEvent;
  eventType: EventType;
  onArchive: () => void;
  isLoading: boolean;
};

function RecordedEventRow({ event, eventType, onArchive, isLoading }: RecordedEventRowProps) {
  const details = eventDetails[eventType];
  const isManager = useAppSelector(selectIsManager);
  return (
    <tr style={{ height: "3.5em" }} className={classNames({ "line-through": event.archived })}>
      <TableCell>
        <FontAwesomeIcon icon={details.icon} title={details.label} />
      </TableCell>
      <TableCell>{format(new Date(event.recorded_at), "h:mm a")}</TableCell>
      <TableCell />
      <TableCell />
      <TableCell>1 event</TableCell>
      <TableCell>{details.label}</TableCell>
      <TableCell />
      <TableCell>
        {isManager && !event.archived && (
          <IconButton
            icon="trash-alt"
            title={`Archive ${details.label}`}
            colour="danger"
            size="lg"
            onClick={onArchive}
            disabled={isLoading}
          />
        )}
      </TableCell>
    </tr>
  );
}

export function OnCallEventRow({ event }: { event: OnCallEvent }) {
  const [archiveEvent, { isLoading }] = useArchiveOnCallEventMutation();
  return (
    <RecordedEventRow
      event={event}
      eventType="on-call"
      onArchive={() => archiveEvent({ onCallEventId: event.id })}
      isLoading={isLoading}
    />
  );
}

export function CallBackEventRow({ event }: { event: CallBackEvent }) {
  const [archiveEvent, { isLoading }] = useArchiveCallBackEventMutation();
  return (
    <RecordedEventRow
      event={event}
      eventType="call-back"
      onArchive={() => archiveEvent({ callBackEventId: event.id })}
      isLoading={isLoading}
    />
  );
}

type NewRecordedEventRowProps = {
  defaultRecordedAt: Date;
  eventType: EventType;
  onSave: (recordedAt: Date) => Promise<unknown>;
  onCancel: () => void;
  isLoading: boolean;
};

function NewRecordedEventRow({ defaultRecordedAt, eventType, onSave, onCancel, isLoading }: NewRecordedEventRowProps) {
  const details = eventDetails[eventType];
  const [recordedAt, setRecordedAt] = React.useState<Date | null>(defaultRecordedAt);
  const recordedAtRef = React.useRef<Date | null>(defaultRecordedAt);
  const savingRef = React.useRef(false);

  function updateRecordedAt(value: Date | null) {
    recordedAtRef.current = value;
    setRecordedAt(value);
  }

  async function save(recordedAt: Date | null) {
    if (!recordedAt || savingRef.current) return;
    savingRef.current = true;
    try {
      await onSave(recordedAt);
      onCancel();
    } catch {
      savingRef.current = false;
    }
  }

  function submitOnEnter(event: React.KeyboardEvent<HTMLDivElement>) {
    // TimeInput handles Enter first, updating the ref before the event bubbles here.
    if (event.key === "Enter") {
      save(recordedAtRef.current);
    }
  }

  return (
    <tr>
      <TableCell>
        <FontAwesomeIcon icon={details.icon} title={details.label} />
      </TableCell>
      <TableCell>
        <div className="absolute top-0 left-0 h-full w-full" onKeyDown={submitOnEnter}>
          <TimeInput
            type="timesheet"
            initialValue={defaultRecordedAt}
            onEnter={updateRecordedAt}
            autoFocus
            disabled={isLoading}
          />
        </div>
      </TableCell>
      <TableCell />
      <TableCell />
      <TableCell>1 event</TableCell>
      <TableCell>{details.label}</TableCell>
      <TableCell />
      <TableCell>
        <Button className="mr-1" size="sm" onClick={() => save(recordedAt)} disabled={isLoading || !recordedAt}>
          Record
        </Button>
        <IconButton
          icon="times"
          title="Cancel"
          colour="danger"
          size="lg"
          onMouseDown={(event) => event.preventDefault()}
          onClick={onCancel}
          disabled={isLoading}
        />
      </TableCell>
    </tr>
  );
}

type NewEventProps = {
  employee: Employee;
  defaultRecordedAt: Date;
  onCancel: () => void;
};

export function NewOnCallEventRow({ employee, defaultRecordedAt, onCancel }: NewEventProps) {
  const [createEvent, { isLoading }] = useCreateOnCallEventMutation();
  return (
    <NewRecordedEventRow
      defaultRecordedAt={defaultRecordedAt}
      eventType="on-call"
      onSave={(recordedAt) => createEvent({ employee_id: employee.id, recorded_at: formatISO(recordedAt) }).unwrap()}
      onCancel={onCancel}
      isLoading={isLoading}
    />
  );
}

export function NewCallBackEventRow({ employee, defaultRecordedAt, onCancel }: NewEventProps) {
  const [createEvent, { isLoading }] = useCreateCallBackEventMutation();
  return (
    <NewRecordedEventRow
      defaultRecordedAt={defaultRecordedAt}
      eventType="call-back"
      onSave={(recordedAt) => createEvent({ employee_id: employee.id, recorded_at: formatISO(recordedAt) }).unwrap()}
      onCancel={onCancel}
      isLoading={isLoading}
    />
  );
}
